import { CommonModule } from '@angular/common';
import { Component, forwardRef, Input } from '@angular/core';
import { FormsModule, ReactiveFormsModule, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';
import { DimensionsListenerDirective } from '@games/shared/game-dynamic-blocks/dimensions-listener.directive';
import { MathUtils } from '../../utils';
import { MathOperation, MathOperationWritable } from '../math-operation';
import { FocusFieldDirective } from '@games/core/focus';
import { InputWithCalculatorDirective } from '@games/core/focus/InputWithCalculator';
import { VirtualKeyboardDirective } from '@games/core/input/VirtualKeyboard';

@Component({
  selector: 'ms-math-matrix',
  templateUrl: './math-matrix.component.html',
  styleUrls: ['./math-matrix.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FocusFieldDirective,
    VirtualKeyboardDirective,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MathMatrixComponent),
      multi: true,
    },
  ],
  hostDirectives: [DimensionsListenerDirective],
})
export class MathMatrixComponent<T = string> extends MathOperationWritable {
  readonly valueControl = new FormControl<T[][]>([]);

  private _isEmpty: boolean = true;

  readonly strokeWidth = 6;

  @Input()
  public get value(): T[][] {
    return this._value;
  }

  public set value(value: T[][]) {
    if (value === this._value) return;
    this._value = value;
    this._isEmpty = !value.length || !value[0].length;
    this.updateViewBox();
    this.valueControl.setValue(value);
  }

  private _value: T[][] = [];

  @Input()
  public get useTextInput(): boolean {
    return this._useTextInput;
  }

  public set useTextInput(value: boolean) {
    this._useTextInput = value;
    if (value) {
      this.value = this.valueControl.value;
      this.updateViewBox();
    }
  }

  private _useTextInput: boolean = false;

  constructor() {
    super();
    this.valueControl.valueChanges.subscribe((value) => {
      this.value = value;
      this.updateViewBox();
      this.emitChanges();
    });
  }

  writeValue(value: T[][]): void {
    if (value) {
      this.value = value;
      this.valueControl.setValue(value);
      this.updateViewBox();
    }
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.valueControl.disable();
    } else {
      this.valueControl.enable();
    }
  }

  private updateViewBox(): void {
    if (this._isEmpty) {
      this._setViewBox(40, 40);
      return;
    }

    const cellSize = this._getMaxCellSize() + this.fontSize / 2; // Сразу добавили padding

    const width = cellSize * this.value[0].length;
    const height = cellSize * this.value.length;

    this._setViewBox(width, height);
  }

  private _getMaxCellSize() {
    let maxWidth = 40;
    this.value.forEach((row) => {
      row.forEach((number) => {
        const numberWidth = MathUtils.getTextWidth(number.toString(), {
          size: this.fontSize,
          family: this.fontFamily,
          weight: this.fontWeight,
        });
        if (numberWidth > maxWidth) {
          maxWidth = numberWidth;
        }
      });
    });
    return maxWidth;
  }

  emitChanges() {
    this.onChange(this.value.toString());
  }
}
